import React, { useEffect, useState } from 'react';
import { Instance } from '../model/Instance';
import { Company } from '../model/Company';
import { checkEmailVerificationCode, getCompany, saveCompany, saveCompanyEmail, validateEmail } from '../services/StudioService';
import { Spinner } from './Spinner';
import { VerificationCodeModal } from './VerificationCodeModal';

const CompanyInformation = ({ instance }: Props) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Company | null>(null);
  const [draft, setDraft] = useState<Company | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  useEffect(() => {
    setLoading(true);
    getCompany(instance).then(result => {
      setData(result);
      setDraft(result);
      setLoading(false);
    });
  }, [instance]);

  const onEdit = () => {
    setEditing(true);
  }

  const onTrySave = async () => {
    if(!draft?.invoiceEmailValid || draft.invoiceEmail !== data?.invoiceEmail) {
      await validateEmail(instance, draft?.invoiceEmail || '');
      setShowVerificationModal(true);
    }
    else {
      await onSave();
    }
  }

  const onCheckCode = async (code: string) => {
    return await checkEmailVerificationCode(instance, code);
  }

  const onSave = async (code: string = '') => {
    setShowVerificationModal(false);
    setLoading(true);
    if(code) {
      await saveCompanyEmail(instance, draft?.invoiceEmail || '', code);
      setDraft(prev => ({ ...prev!, invoiceEmailValid: true }));
    }
    await saveCompany(instance, draft!);
    setData(draft);
    setEditing(false);
    setLoading(false);
  };

  const onCancel = () => {
    setDraft(data);
    setEditing(false);
  };

  const onChange = (field: keyof Company, value: string) => {
    setDraft(prev => ({ ...prev!, [field]: value }));
    if(field === 'invoiceEmail') {
      setDraft(prev => ({ ...prev!, invoiceEmailValid: false }));
    }
  };

  const isComplete = !!draft?.billingAddress?.trim() && !!draft?.invoiceEmail?.trim() && !!draft?.city?.trim() && !!draft?.postalCode?.trim() && !!draft?.country?.trim();
  if(!draft) {
    return <Spinner loading={loading} />
  };
  return (
    <>
      <h5 className="mb-4">Legal Profile</h5>
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label text-muted">Company legal name</label>
          <input type="text" className="form-control" value={draft.legalName ?? ''} readOnly />
        </div>
        <div className="col-md-6">
          <label className="form-label text-muted">VAT / Tax number</label>
          <input type="text" className="form-control" value={draft.vatNumber ?? ''} readOnly />
        </div>
      </div>
      <p className="fw-semibold mb-3 mt-4" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>INVOICE DETAILS (EDITABLE)</p>
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Billing address*</label>
          <input type="text" className="form-control" value={draft.billingAddress ?? ''} disabled={!editing} onChange={e => onChange('billingAddress', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label">Invoice email*</label>
          <div className="d-flex align-items-center">
            <input type="email" className="form-control" value={draft.invoiceEmail ?? ''} disabled={!editing} onChange={e => onChange('invoiceEmail', e.target.value)} />
            <span className={`badge bg-label-${draft.invoiceEmailValid ? 'success' : 'danger'} ms-2`}>{draft.invoiceEmailValid ? 'Verified' : 'Pending'}</span>
          </div>
        </div>
      </div>
      <div className="row mb-4">
        <div className="col-md-4">
          <label className="form-label">City*</label>
          <input type="text" className="form-control" value={draft.city ?? ''} disabled={!editing} onChange={e => onChange('city', e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Postal Code*</label>
          <input type="text" className="form-control" value={draft.postalCode ?? ''} disabled={!editing} onChange={e => onChange('postalCode', e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Country*</label>
          <input type="text" className="form-control" value={draft.country ?? ''} disabled={!editing} onChange={e => onChange('country', e.target.value)} />
        </div>
      </div>
      {!editing && (
        <button type="button" className="btn btn-primary" onClick={onEdit}>Edit</button>
      )}
      {editing && (
        <>
          <button type="button" className="btn btn-primary" disabled={!isComplete} onClick={onTrySave}>Save Changes</button>
          <button type="button" className="btn bg-danger-subtle text-danger ms-2" onClick={onCancel}>Cancel</button>
        </>
      )}
      <VerificationCodeModal show={showVerificationModal} onCheck={onCheckCode} onNext={onSave} onCancel={onSave} />
      <Spinner loading={loading} />
    </>
  );
}

interface Props {
  instance: Instance;
}

export { CompanyInformation };
